/**
 * Social sharing utilities for SunSquatcher
 */

export interface ShareData {
  location: string;
  date: string;
  type: "sunrise" | "sunset";
  quality: string;
  score: number;
  time?: string;
}

/**
 * Generate shareable text for social media
 */
export function generateShareText(data: ShareData): string {
  const emoji = data.type === "sunrise" ? "🌅" : "🌇";
  const qualityEmoji =
    data.quality === "Vivid" ? "🌟" :
    data.quality === "Great" ? "✨" :
    data.quality === "Good" ? "⭐" : "📸";

  return `${emoji} ${qualityEmoji} ${data.quality} ${data.type} predicted for ${data.location} on ${data.date}! Quality score: ${data.score}%${data.time ? ` at ${data.time}` : ""}\n\nDiscover your perfect golden hour with SunSquatcher 🌄`;
}

/**
 * Share to Twitter (X)
 */
export function shareToTwitter(data: ShareData): void {
  const text = generateShareText(data);
  const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
  window.open(url, '_blank', 'noopener,noreferrer,width=550,height=420');
}

/**
 * Share to Facebook
 */
export function shareToFacebook(data: ShareData): void {
  const text = generateShareText(data);
  // Facebook doesn't support pre-filled text via URL, so we use the share dialog
  const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}&quote=${encodeURIComponent(text)}`;
  window.open(url, '_blank', 'noopener,noreferrer,width=550,height=420');
}

/**
 * Share to WhatsApp
 */
export function shareToWhatsApp(data: ShareData): void {
  const text = generateShareText(data);
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Use different URL for mobile vs desktop
  const baseUrl = isMobile ? 'whatsapp://send' : 'https://web.whatsapp.com/send';
  const url = `${baseUrl}?text=${encodeURIComponent(text)}`;

  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Native Web Share API (fallback for mobile devices)
 */
export async function nativeShare(data: ShareData): Promise<boolean> {
  if (!navigator.share) {
    return false;
  }

  try {
    await navigator.share({
      title: `${data.quality} ${data.type} at ${data.location}`,
      text: generateShareText(data),
    });
    return true;
  } catch {
    // User cancelled or share failed - this is expected behavior
    return false;
  }
}
